// Module 09's half of capable-to-promise (P3-J J-2, D-465, spec 09 §4.4 and
// §4.7). The composer's Check date and Use in quote, and the order detail's
// promise chip and its two pending states, read their facts through these
// pure functions so each rule is stated once and unit-tested without a
// screen.
//
// The dates themselves are module 03's. This file never computes one: it
// turns a quoted promise into the line fields the save sends, reads the
// save's refusal back onto its line, and decides which of module 09's own
// columns a line or an order is showing.

import { RequestError } from "../../../../lib/request";
import type { CtpQuoteResult } from "../../planning/public";
import type { OrderDetail, QuoteLineInput } from "../hooks/quote-queries";

type OrderLine = OrderDetail["lines"][number];

/** Why the save refused a line's promise (`QUOTE_LINE_PROMISE_INVALID`). */
export const QUOTE_LINE_PROMISE_REASONS = [
  "not_found",
  "not_quoted",
  "expired",
  "product_mismatch",
  "qty_mismatch",
] as const;
export type QuoteLinePromiseReason = (typeof QUOTE_LINE_PROMISE_REASONS)[number];

/**
 * Every refused line (1-based) and why, or undefined for any other answer.
 * The save names every stale line in `lines` (spec 09 §5); an answer from a
 * build before that names one, in `line_no` and `reason`.
 */
export function quoteLinePromiseRefusal(
  error: unknown,
): readonly { readonly lineNo: number; readonly reason: QuoteLinePromiseReason }[] | undefined {
  if (!(error instanceof RequestError) || error.code !== "QUOTE_LINE_PROMISE_INVALID") {
    return undefined;
  }
  const problem = (error.problem ?? {}) as Record<string, unknown>;
  const extensions = problem["extensions"] as Record<string, unknown> | undefined;
  // A reason this build does not know still names its line; it reads as the
  // generic "no longer a quoted date" sentence.
  const known = (reason: unknown): QuoteLinePromiseReason =>
    QUOTE_LINE_PROMISE_REASONS.find((r) => r === reason) ?? "not_quoted";
  const listed = problem["lines"] ?? extensions?.["lines"];
  if (Array.isArray(listed)) {
    const out = (listed as unknown[]).flatMap((entry) => {
      const e = (entry ?? {}) as Record<string, unknown>;
      return typeof e["line_no"] === "number"
        ? [{ lineNo: e["line_no"], reason: known(e["reason"]) }]
        : [];
    });
    if (out.length > 0) return out;
  }
  const lineNo = problem["line_no"] ?? extensions?.["line_no"];
  if (typeof lineNo !== "number") return undefined;
  return [{ lineNo, reason: known(problem["reason"] ?? extensions?.["reason"]) }];
}

/**
 * The fulfilment plan Use in quote writes (D-465 ruling 5): the promise's
 * off-cut pieces as one §2.14 `offcut_combo`, so the line prices from the
 * off-cut class beside the fresh-cable price. Undefined when the promise does
 * not rest on off-cuts, and the line keeps whatever plan it had.
 */
export function offcutPlanOf(quote: CtpQuoteResult): QuoteLineInput["fulfillment_plan"] {
  const pieces = quote.basis.atp.offcut_combination;
  if (quote.fulfillment_path !== "offcut" || pieces.length === 0) return undefined;
  return [
    {
      kind: "offcut_combo",
      pieces: pieces.map((p) => ({ stock_unit_id: p.stock_unit_id, length: p.length_m })),
    },
  ] as QuoteLineInput["fulfillment_plan"];
}

/** Two exact decimal strings name the same number ("100", "100.000"). */
export function sameDecimal(a: string, b: string): boolean {
  const norm = (v: string): string | undefined => {
    const m = /^(\d+)(?:\.(\d*))?$/.exec(v.trim());
    if (m === null) return undefined;
    const whole = (m[1] ?? "").replace(/^0+(?=\d)/, "");
    const frac = (m[2] ?? "").replace(/0+$/, "");
    return frac.length > 0 ? `${whole}.${frac}` : whole;
  };
  const left = norm(a);
  return left !== undefined && left === norm(b);
}

/**
 * The line fields Use in quote writes. The line takes the checked quantity in
 * the product's base unit, because the save holds a promise to exactly the
 * line's quantity (`qty_mismatch` otherwise) and the dialog checks in the base
 * unit. A line already in that unit at that quantity keeps its own text.
 *
 * A typed price is per unit, so a line moved onto another unit drops it
 * (`priceText: ""`) and the engine prices the line again in the new unit;
 * `priceReset` makes the grid say so. Keeping the figure would have priced
 * 0.1 km at 5,000,000 per km as 100 m at 5,000,000 per m.
 */
export function linePatchFromQuote(
  row: { readonly qtyText: string; readonly uomId: string; readonly priceText: string },
  quote: CtpQuoteResult,
  baseUomId: string | undefined,
): {
  readonly promiseId: string;
  readonly promiseHint: string;
  readonly qtyText: string;
  readonly uomId: string;
  readonly plan?: QuoteLineInput["fulfillment_plan"];
  readonly priceText?: string;
  readonly priceReset?: boolean;
} {
  const uomId = baseUomId ?? row.uomId;
  const keepsText = uomId === row.uomId && sameDecimal(row.qtyText, quote.qty);
  const plan = offcutPlanOf(quote);
  const dropsPrice = uomId !== row.uomId && row.priceText.trim().length > 0;
  return {
    promiseId: quote.id,
    promiseHint: quote.promised_date,
    qtyText: keepsText ? row.qtyText : quote.qty,
    uomId,
    ...(plan !== undefined ? { plan } : {}),
    ...(dropsPrice ? { priceText: "", priceReset: true } : {}),
  };
}

/** Order statuses whose lines module 03 commits a promise onto (D-462 ruling 4). */
const PROMISABLE_ORDER = new Set(["confirmed", "in_production", "ready"]);
/** A credit check that holds the order: no promise until the release (D-465 ruling 1). */
const CREDIT_BLOCKED = new Set(["blocked_credit", "blocked_prepay"]);
/** Line statuses that can still be promised: neither cancelled nor shipped. */
const PROMISABLE_LINE = new Set(["open", "in_production", "ready"]);

/**
 * What a line's date reads as on the order detail.
 *
 * - `dated`: the line carries `line_promise_date` (typed, imported or stamped).
 * - `checking`: no date yet, and the order's Confirmed message is waiting for
 *   the dates (`confirmed_notice_due_at` set), so planning is promising it.
 * - `none`: neither.
 */
export function linePromiseState(
  order: Pick<OrderDetail, "confirmed_notice_due_at">,
  line: Pick<OrderLine, "line_promise_date" | "line_status">,
): "dated" | "checking" | "none" {
  if (line.line_promise_date !== null) return "dated";
  if (line.line_status === "cancelled" || line.line_status === "shipped") return "none";
  // `?? null`: a payload from an API build before J-2 (a deploy roll) has
  // neither field, and reads as nothing pending.
  return (order.confirmed_notice_due_at ?? null) !== null ? "checking" : "none";
}

/** Order statuses whose dates are still to come: planning may yet date them. */
const DATABLE_ORDER = new Set(["pending_approval", "confirmed", "in_production", "ready"]);

/**
 * Whether the order detail says "The promise date is set by planning." (spec
 * 09 §4.7, D-425's copy): only while the order has no date and can still get
 * one. Beside a date it would read as a second, unexplained answer, and on a
 * shipped, delivered, closed or cancelled order there is nothing left to set.
 */
export function orderPromisePending(order: Pick<OrderDetail, "promise_date" | "status">): boolean {
  return order.promise_date === null && DATABLE_ORDER.has(order.status);
}

/**
 * Whether the order detail offers Check date on a line: an undated, live line
 * of a confirmed order the credit check does not hold, that no live promise
 * backs, while planning is not already promising it. The dialog then commits
 * onto this line (row 75); module 03 refuses a credit-blocked order's line
 * with 409 ORDER_LINE_NOT_PROMISABLE (`credit_blocked`).
 */
export function lineCheckable(
  order: Pick<OrderDetail, "status" | "confirmed_notice_due_at" | "credit_check_status">,
  line: Pick<OrderLine, "line_promise_date" | "line_status" | "promise_status">,
): boolean {
  if (!PROMISABLE_ORDER.has(order.status)) return false;
  if (CREDIT_BLOCKED.has(order.credit_check_status)) return false;
  if (!PROMISABLE_LINE.has(line.line_status)) return false;
  if (line.line_promise_date !== null) return false;
  if ((line.promise_status ?? null) !== null) return false;
  return (order.confirmed_notice_due_at ?? null) === null;
}

/**
 * Whether the order detail offers Re-promise on a line (spec 09 §4.7, D-469):
 * a live line still to ship, of a confirmed, in-production or ready order the
 * credit check does not hold, that already carries a date: a committed, at-
 * risk or broken promise, or a typed or imported date, which re-promise
 * converts. A kept promise is done with. An undated line is Check date's
 * (`lineCheckable`), never Re-promise's. The caller adds the code
 * (`sales.order.re_promise`) and the desktop rule.
 */
export function lineRePromisable(
  order: Pick<OrderDetail, "status" | "credit_check_status">,
  line: Pick<OrderLine, "line_promise_date" | "line_status" | "promise_status">,
): boolean {
  if (!PROMISABLE_ORDER.has(order.status)) return false;
  if (CREDIT_BLOCKED.has(order.credit_check_status)) return false;
  if (!PROMISABLE_LINE.has(line.line_status)) return false;
  const status = line.promise_status ?? null;
  if (status === "kept") return false;
  return line.line_promise_date !== null || status !== null;
}

/**
 * A line's open quantity (ordered less shipped) as exact decimal text with
 * at most three places, the precision of both columns (numeric(14,3)), or
 * undefined when either is not a plain decimal or nothing is left to ship.
 */
export function openQtyText(qtyOrdered: string, qtyShipped: string): string | undefined {
  const milli = (v: string): bigint | undefined => {
    const m = /^(\d+)(?:\.(\d{0,3}))?$/.exec(v.trim());
    if (m === null) return undefined;
    return BigInt(m[1] ?? "0") * 1000n + BigInt((m[2] ?? "").padEnd(3, "0") || "0");
  };
  const ordered = milli(qtyOrdered);
  const shipped = milli(qtyShipped);
  if (ordered === undefined || shipped === undefined) return undefined;
  const open = ordered - shipped;
  if (open <= 0n) return undefined;
  const whole = open / 1000n;
  const frac = (open % 1000n).toString().padStart(3, "0").replace(/0+$/, "");
  return frac.length > 0 ? `${whole.toString()}.${frac}` : whole.toString();
}

/** The chip variant of a line's promise mirror. */
export function promiseStatusVariant(
  status: NonNullable<OrderLine["promise_status"]>,
): "ok" | "warn" | "bad" | "neutral" {
  switch (status) {
    case "committed":
      return "ok";
    case "at_risk":
      return "warn";
    case "broken":
      return "bad";
    case "kept":
      return "neutral";
  }
}
